import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Star, Stethoscope, Bell, Pill, Search } from "lucide-react";
import pillsImage from "../assets/icons/pills.png";
import Skeleton from "@mui/material/Skeleton";
import api from "../lib/api";
import { useTheme } from "../theme";
import { getCurrentCoordinates } from "../lib/nativeGeolocation";

const DEFAULT_MEDICATION_VISIBILITY_WINDOW_MINUTES = 120;

const parseClockTimeToMinutes = (value) => {
  const match = String(value || "").match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (
    !Number.isFinite(hours) ||
    !Number.isFinite(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return null;
  }

  return hours * 60 + minutes;
};

const Upcoming_appointments = () => {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const { theme } = useTheme();

  const [appointments, setAppointments] = useState([]);
  const [loadingAppointments, setLoadingAppointments] = useState(true);

  const [location, setLocation] = useState({ lat: null, lon: null });
  const [nearbyDoctors, setNearbyDoctors] = useState([]);
  const [loadingNearby, setLoadingNearby] = useState(true);

  const [pendingTests, setPendingTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tests, setTests] = useState([]);
  const [completedTests, setCompletedTests] = useState([]);
  const [error, setError] = useState("");

  const user_id = localStorage.getItem("userId");
  const effectiveUserId = user_id;

  // Medications
  const [todayMedications, setTodayMedications] = useState([]);
  const [loadingTodayMedications, setLoadingTodayMedications] = useState(true);
  const [medicationActionKey, setMedicationActionKey] = useState("");
  const [medicationNowTick, setMedicationNowTick] = useState(Date.now());

  const loadTodayMedications = async () => {
    try {
      setLoadingTodayMedications(true);

      const res = await api.get("/api/medications");
      const payload = res?.data ?? res;

      let list = [];
      if (Array.isArray(payload)) list = payload;
      else if (Array.isArray(payload?.medications)) list = payload.medications;
      else if (Array.isArray(payload?.data)) list = payload.data;
      else if (Array.isArray(payload?.data?.medications)) list = payload.data.medications;
      else if (Array.isArray(payload?.result)) list = payload.result;

      console.log("=== /api/medications debug ===");
      console.log("payload:", payload);
      console.log("normalized list:", list);
      if (list[0]) {
        console.log("first item:", list[0]);
        console.log("first item keys:", Object.keys(list[0]));
        console.log("first item dosages:", list[0]?.dosages);
      }

      setTodayMedications(list);
    } catch (err) {
      console.error("Failed to fetch medications:", err);
      setTodayMedications([]);
    } finally {
      setLoadingTodayMedications(false);
    }
  };

  // ---------- Doctor card ----------
  function DoctorCard1({ doctor }) {
    const ratingValue = Number(doctor?.rating || 5);

    const styles = {
      card: {
        width: 248,
        minWidth: 248,
        maxWidth: 248,
        minHeight: 314,
        padding: "18px 14px",
        backgroundColor: "var(--surface)",
        borderRadius: 24,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: 14,
        boxShadow: "inset 0 0 0 1px #ececef",
        lineHeight: 1.3,
        border: "1px solid #ededf0",
        fontFamily: "Inter, system-ui, sans-serif",
        boxSizing: "border-box",
        flexShrink: 0,
        scrollSnapAlign: "start",
        cursor: "pointer",
      },
      imageWrapper: {
        width: 106,
        height: 106,
        flexShrink: 0,
        borderRadius: "50%",
        overflow: "hidden",
        background: "#e6e7ec",
      },
      image: {
        width: "100%",
        height: "100%",
        objectFit: "cover",
        borderRadius: "50%",
      },
      name: {
        fontSize: 20,
        fontWeight: 700,
        color: "#121217",
        lineHeight: 1.18,
        width: "100%",
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        overflowWrap: "anywhere",
        wordBreak: "break-word",
        textAlign: "center",
        minHeight: 48,
        maxWidth: 210,
      },
      specialty: {
        fontSize: 16,
        color: "#6b6f7a",
        marginTop: -8,
        textAlign: "center",
        minHeight: 22,
        maxWidth: 210,
        width: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      },
      ratingPill: {
        marginTop: 8,
        minWidth: 92,
        height: 40,
        borderRadius: 999,
        background: "linear-gradient(180deg, #8f2bff 0%, #6f00ff 100%)",
        color: "#fff",
        border: "none",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        padding: "0 18px",
        fontSize: 18,
        fontWeight: 700,
        letterSpacing: "0.01em",
      },
    };

    return (
      <div style={styles.card} onClick={() => navigate(`/doctor-details/${doctor._id}`)}>
        <div style={styles.imageWrapper}>
          <img
            src={doctor.avatar || doctor.image || "https://randomuser.me/api/portraits/men/7.jpg"}
            alt={doctor.name || doctor.fullName || "Doctor"}
            style={styles.image}
          />
        </div>

        <div style={styles.name}>{doctor.name || doctor.fullName || "Dr. Specialist"}</div>
        <div style={styles.specialty}>{doctor.specialty || "General Physician"}</div>

        <div style={styles.ratingPill}>
          <span>
            {Number.isFinite(ratingValue) ? ratingValue.toFixed(1).replace(/\.0$/, "") : "5"}
          </span>
          <Star size={16} color="#ffffff" fill="#ffffff" />
        </div>
      </div>
    );
  }

  // ---------- Nearby doctors ----------
  useEffect(() => {
    const fetchNearby = async () => {
      const { lat, lon } = location;
      if (lat == null || lon == null) return;

      try {
        setLoadingNearby(true);
        const res = await api.get(`/api/doctor/nearby?lat=${lat}&lng=${lon}&radius=5`);
        setNearbyDoctors(res.data);
      } catch (err) {
        console.error("Failed to fetch nearby doctors:", err);
      } finally {
        setLoadingNearby(false);
      }
    };

    fetchNearby();
  }, [user_id, location]);

  // ---------- Location ----------
  useEffect(() => {
    const getLocation = async () => {
      const result = await getCurrentCoordinates();
      if (!result.ok) {
        console.error("Failed to get location:", result.error);
        return;
      }
      setLocation({ lat: result.coords.lat, lon: result.coords.lon });
    };

    getLocation();
  }, []);

  // ---------- Appointments ----------
  useEffect(() => {
    const fetchAppointmentsWithDoctors = async () => {
      if (!effectiveUserId) {
        setAppointments([]);
        setLoadingAppointments(false);
        return;
      }

      try {
        setLoadingAppointments(true);

        const res = await api.get(`/api/consultations/patient/${effectiveUserId}`);
        const appts = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.data?.consultations)
            ? res.data.consultations
            : [];

        const enriched = await Promise.allSettled(
          appts.map(async (appt) => {
            const doctorId = typeof appt.doctor === "object" ? appt.doctor?._id : appt.doctor;
            if (doctorId) {
              try {
                const doctorRes = await api.get(`/api/doctor/${doctorId}`);
                return { ...appt, doctor: doctorRes.data };
              } catch {
                // fall through
              }
            }
            return { ...appt, doctor: appt.doctor_ || appt.doctor || {} };
          })
        );

        const apptsWithDoctor = enriched
          .filter((r) => r.status === "fulfilled")
          .map((r) => r.value);

        apptsWithDoctor.sort(
          (a, b) => new Date(a.appointmentTime) - new Date(b.appointmentTime)
        );

        setAppointments(apptsWithDoctor);
      } catch (err) {
        console.error("Failed to fetch appointments with doctors:", err);
        setAppointments([]);
      } finally {
        setLoadingAppointments(false);
      }
    };

    fetchAppointmentsWithDoctors();
  }, [profile?._id]);

  // ---------- Lab tests / bookings ----------
  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      try {
        setLoading(true);

        const testRes = await api.get("/api/labtests");
        if (mounted && testRes?.data?.tests) {
          setTests(testRes.data.tests);
        }

        if (user_id) {
          const bookingRes = await api
            .get(`/api/lab-bookings/user/${user_id}`)
            .catch(() => null);

          if (bookingRes?.data?.bookings?.length) {
            setPendingTests(bookingRes.data.bookings);
          } else {
            setPendingTests([]);
            setCompletedTests([]);
          }
        }
      } catch (err) {
        console.error("Error loading data:", err);
        setError("Failed to load data.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    return () => {
      mounted = false;
    };
  }, []);

  // ---------- Medications ----------
  useEffect(() => {
    loadTodayMedications();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setMedicationNowTick(Date.now());
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  // ---------- Derived ----------
  const upcomingAppointments = [...appointments]
    .filter((appointment) => {
      const status = (appointment?.status || "").toLowerCase();
      return ["scheduled", "ongoing"].includes(status);
    })
    .sort((left, right) => new Date(left.appointmentTime) - new Date(right.appointmentTime));

  const upcomingAppointment = upcomingAppointments[0] || null;
  const upcomingAppointmentCount = upcomingAppointments.length;

  const nextLabBooking =
    [...pendingTests]
      .filter((booking) => {
        const status = (booking?.tests?.[0]?.result?.status || "pending").toLowerCase();
        return status !== "completed";
      })
      .sort(
        (left, right) =>
          new Date(left?.bookingDate || left?.createdAt || 0) -
          new Date(right?.bookingDate || right?.createdAt || 0)
      )[0] || null;

  // ---------- Flatten medications -> individual dose entries ----------
  // Backend schema (from your route file):
  //   UserMedication { _id, medicineName, dosages: [{ time, taken, takenAt, notes }] }
  //   dosage.time is the exact string ("08:00 AM") required by mark-dose / mark-missed.
  const medicationEntries = todayMedications
    .flatMap((medication) => {
      // Prefer the real `dosages` array from your schema, then fall back to
      // other common shapes so this never blanks out.
      let rawDoses =
        medication?.dosages ||
        medication?.scheduledTimes ||
        medication?.times ||
        medication?.doses ||
        medication?.reminders ||
        medication?.schedule ||
        [];

      if (typeof rawDoses === "string") {
        rawDoses = rawDoses
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean);
      }

      if (rawDoses && !Array.isArray(rawDoses) && typeof rawDoses === "object") {
        rawDoses = [rawDoses];
      }

      if (!rawDoses || rawDoses.length === 0) {
        rawDoses = [{ time: medication?.time || "--:--" }];
      }

      return rawDoses.map((dose, index) => {
        const doseObj = typeof dose === "string" ? { time: dose } : dose || {};

        // IMPORTANT: keep dosageTime EXACTLY as the backend stores it.
        // The mark-dose / mark-missed routes match `d.time === dosageTime`.
        const dosageTime =
          doseObj.time ||
          doseObj.effectiveTime ||
          doseObj.scheduledTime ||
          medication?.time ||
          "--:--";

        const takenToday = Boolean(
          doseObj.taken &&
            (!doseObj.takenAt ||
              new Date(doseObj.takenAt).toDateString() === new Date().toDateString())
        );

        const skippedToday = Boolean(doseObj.skippedToday || doseObj.skipped);
        const missedToday = Boolean(doseObj.missed);
        const resolved = takenToday || skippedToday || missedToday;

        const visibilityWindowMinutesRaw =
          medication?.reminderWindowMinutes ??
          medication?.displayDurationMinutes ??
          DEFAULT_MEDICATION_VISIBILITY_WINDOW_MINUTES;
        const visibilityWindowMinutes = Number(visibilityWindowMinutesRaw);

        return {
          id: `${medication._id || medication.medicationId || "med"}-${index}`,
          medicationId: medication._id || medication.medicationId,
          name:
            medication.medicineName ||
            medication.name ||
            medication.medicationName ||
            "Medication",
          dosage: medication.dosage || medication.dose,
          frequency: medication.frequency,
          reason: medication.reason || medication.purpose || medication.notes,
          time: dosageTime, // display
          dosageTime, // exact value sent to backend
          timeIndex: index,
          taken: takenToday,
          skippedToday: skippedToday || missedToday,
          resolved,
          visibilityWindowMinutes:
            Number.isFinite(visibilityWindowMinutes) && visibilityWindowMinutes > 0
              ? visibilityWindowMinutes
              : DEFAULT_MEDICATION_VISIBILITY_WINDOW_MINUTES,
        };
      });
    })
    .sort((left, right) => left.time.localeCompare(right.time));

  const now = new Date(medicationNowTick);
  const nowMinutes = now.getHours() * 60 + now.getMinutes();

  const pendingMedicationEntries = medicationEntries.filter((medication) => {
    if (medication.resolved) return false;

    const dueMinutes = parseClockTimeToMinutes(medication.time);
    if (dueMinutes == null) return true;

    return nowMinutes <= dueMinutes + medication.visibilityWindowMinutes;
  });

  const medicationsToRender =
    pendingMedicationEntries.length > 0 ? pendingMedicationEntries : medicationEntries;

  console.log("=== RENDER DEBUG ===");
  console.log("todayMedications.length:", todayMedications.length);
  console.log("medicationEntries.length:", medicationEntries.length);
  console.log("pendingMedicationEntries.length:", pendingMedicationEntries.length);

  const formatScheduleLabel = (value) => {
    const date = value ? new Date(value) : null;
    if (!date || Number.isNaN(date.getTime())) return "Schedule unavailable";

    const isToday = date.toDateString() === new Date().toDateString();
    return `${isToday ? "Today" : date.toLocaleDateString([], { month: "short", day: "numeric" })} · ${date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
  };

  // ---------- Handlers (wired to real backend routes) ----------

  // POST /api/medications/:id/mark-dose
  const handleMedicationTaken = async (medication) => {
    try {
      setMedicationActionKey(medication.id);
      await api.post(`/api/medications/${medication.medicationId}/mark-dose`, {
        dosageTime: medication.dosageTime,
      });
      await loadTodayMedications();
    } catch (err) {
      console.error("Failed to mark dose taken:", err);
    } finally {
      setMedicationActionKey("");
    }
  };

  // POST /api/medications/:id/mark-missed
  const handleMedicationSkip = async (medication) => {
    try {
      setMedicationActionKey(`${medication.id}-skip`);
      await api.post(`/api/medications/${medication.medicationId}/mark-missed`, {
        dosageTime: medication.dosageTime,
        reason: "Skipped by user",
      });
      await loadTodayMedications();
    } catch (err) {
      console.error("Failed to mark dose missed:", err);
    } finally {
      setMedicationActionKey("");
    }
  };

  // POST /api/medications/:id/complete
  const handleMedicationComplete = async (medication) => {
    try {
      setMedicationActionKey(`${medication.id}-complete`);
      await api.post(`/api/medications/${medication.medicationId}/complete`);
      await loadTodayMedications();
    } catch (err) {
      console.error("Failed to complete medication:", err);
    } finally {
      setMedicationActionKey("");
    }
  };

  const appointmentDate = upcomingAppointment?.appointmentTime
    ? new Date(upcomingAppointment.appointmentTime)
    : null;
  const appointmentDayLabel =
    appointmentDate && !Number.isNaN(appointmentDate.getTime())
      ? appointmentDate.toLocaleDateString([], { weekday: "short", day: "numeric", month: "short" })
      : "No appointment scheduled";
  const appointmentTimeLabel =
    appointmentDate && !Number.isNaN(appointmentDate.getTime())
      ? appointmentDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      : "--:--";

  const doctorName = upcomingAppointment?.doctor?.name || "Doctor consultation";
  const doctorSpecialty =
    upcomingAppointment?.doctor?.specialty || upcomingAppointment?.reason || "Orthopedics";
  const doctorRating = upcomingAppointment?.doctor?.rating || "4.5";
  const doctorReviewCount = upcomingAppointment?.doctor?.reviewsCount || "128";

  const exploreDoctorAvatars = (nearbyDoctors || [])
    .map((doctor) => doctor?.avatar || doctor?.image || doctor?.photo || doctor?.profilePicture)
    .filter(Boolean)
    .slice(0, 5);

  const fallbackDoctorAvatars = [
    "https://randomuser.me/api/portraits/women/44.jpg",
    "https://randomuser.me/api/portraits/men/32.jpg",
    "https://randomuser.me/api/portraits/women/68.jpg",
    "https://randomuser.me/api/portraits/men/71.jpg",
    "https://randomuser.me/api/portraits/women/24.jpg",
  ];

  const doctorAvatarStrip = (
    exploreDoctorAvatars.length > 0 ? exploreDoctorAvatars : fallbackDoctorAvatars
  ).slice(0, 5);

  const doctorImage =
    upcomingAppointment?.doctor?.avatar ||
    upcomingAppointment?.doctor?.image ||
    upcomingAppointment?.doctor?.photo ||
    upcomingAppointment?.doctor?.profilePicture ||
    upcomingAppointment?.doctor_?.avatar ||
    upcomingAppointment?.doctor_?.image ||
    upcomingAppointment?.doctor_?.photo ||
    "/avatar.png";

  return (
    <div>
      <h4 style={{ fontWeight: 700, marginBottom: 10, fontSize: 17, color: "#1f2937" }}>
        Doctor Appointment
      </h4>

      <div
        style={{
          background: "white",
          borderRadius: 24,
          border: "1px solid white",
          padding: 12,
          marginBottom: 18,
        }}
      >
        {loadingAppointments ? (
          <Skeleton variant="rectangular" height={124} sx={{ borderRadius: 3 }} />
        ) : !upcomingAppointment ? (
          <div
            style={{
              background: "white",
              borderRadius: 20,
              border: "1px solid #d8dde6",
              padding: 16,
            }}
          >
            <div style={{ display: "flex", justifyContent: "center", gap: 8, marginBottom: 14 }}>
              {doctorAvatarStrip.map((avatar, index) => (
                <img
                  key={`${avatar}-${index}`}
                  src={avatar}
                  alt="doctor"
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    objectFit: "cover",
                    border: "2px solid #e7e9ee",
                  }}
                />
              ))}
            </div>

            <div
              style={{
                textAlign: "center",
                color: "#6b7280",
                fontSize: 15,
                fontWeight: 500,
                marginBottom: 14,
              }}
            >
              You don't have any doctor appointment.
            </div>

            <div style={{ borderTop: "1px solid #dce1e8", paddingTop: 14, textAlign: "center" }}>
              <button
                onClick={() => navigate("/doctor-consult")}
                style={{
                  border: "none",
                  background: "transparent",
                  color: "var(--primary)",
                  fontSize: 15,
                  fontWeight: 600,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                Explore Doctor <Search size={16} />
              </button>
            </div>
          </div>
        ) : (
          <div
            style={{
              background: "#f2f4f7",
              borderRadius: 20,
              border: "1px solid #d8dde6",
              padding: 12,
            }}
          >
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <img
                src={doctorImage}
                alt="doctor"
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: "50%",
                  objectFit: "cover",
                  border: "1px solid #d7dce5",
                }}
              />

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11, color: "#8b94a3", marginBottom: 2 }}>
                  {appointmentDayLabel}, {appointmentTimeLabel}
                </div>
                <div
                  style={{
                    fontSize: "clamp(22px, 7.5vw, 31px)",
                    fontWeight: 700,
                    lineHeight: 1.12,
                    color: "#20242d",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {doctorName}
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontSize: 13,
                    color: "#5f6776",
                  }}
                >
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                    <Stethoscope size={12} style={{ marginTop: -1 }} /> {doctorSpecialty}
                  </span>
                  <span
                    style={{
                      color: "#f0b90b",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    <Star size={12} fill="#f0b90b" /> {doctorRating}
                  </span>
                  <span>({doctorReviewCount})</span>
                  <ArrowRight size={14} color="#7f8796" />
                </div>
              </div>
            </div>

            <div style={{ marginTop: 8, fontSize: 12, color: "#616a79" }}>
              {upcomingAppointment?.reason || "Ankle Pain Consultation"}
            </div>

            <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
              <button
                onClick={() => {
                  if (!upcomingAppointment) {
                    navigate("/doctor-consult");
                    return;
                  }
                  navigate("/reschedule", { state: { consultation: upcomingAppointment } });
                }}
                style={{
                  flex: 1,
                  borderRadius: 14,
                  border: "1px solid #d6c8ff",
                  background: "#f3efff",
                  color: "var(--primary)",
                  padding: "7px 0",
                  fontWeight: 600,
                  fontSize: 14,
                }}
              >
                Reschedule
              </button>

              <button
                onClick={() => {
                  if (!upcomingAppointment) {
                    navigate("/doctor-consult");
                    return;
                  }
                  navigate("/cancel", { state: { consultation: upcomingAppointment } });
                }}
                style={{
                  flex: 1,
                  borderRadius: 14,
                  border: "1px solid #f7c8cd",
                  background: "#fff1f2",
                  color: "#e35a6a",
                  padding: "7px 0",
                  fontWeight: 600,
                  fontSize: 14,
                }}
              >
                Cancel
              </button>
            </div>

            <div
              style={{
                marginTop: 10,
                borderRadius: 12,
                border: "1px solid #f7bf5f",
                background: "#fff9ee",
                padding: "9px 10px",
                fontSize: 12,
                color: "#96733f",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <Bell size={14} color="#e09a29" />
              You have {upcomingAppointmentCount} upcoming appointment
              {upcomingAppointmentCount === 1 ? "" : "s"}! Make sure you are ready.
            </div>
          </div>
        )}
      </div>

      {/* Medications */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 10,
        }}
      >
        <h4 style={{ fontWeight: 700, fontSize: 17, margin: 0, color: "#1f2937" }}>Medications</h4>
        <button
          onClick={() => navigate("/remote")}
          style={{
            border: "none",
            background: "transparent",
            color: "var(--primary)",
            fontWeight: 600,
            fontSize: 16,
            padding: 0,
          }}
        >
          See All
        </button>
      </div>

      <div
        style={{
          background: "white",
          borderRadius: 24,
          border: "1px solid white",
          padding: 12,
          marginBottom: 16,
        }}
      >
        {loadingTodayMedications ? (
          <Skeleton variant="rectangular" height={132} sx={{ borderRadius: 3 }} />
        ) : medicationEntries.length > 0 ? (
          <>
            {medicationsToRender.slice(0, 3).map((medication, index) => (
              <div
                key={medication.id}
                style={{ padding: index === 0 ? "0 0 10px" : "10px 0 0" }}
              >
                {index > 0 && (
                  <div style={{ borderTop: "1px solid #dce1e8", marginBottom: 10 }} />
                )}

                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: "50%",
                      border: "1px solid white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#9ca3af",
                      flexShrink: 0,
                    }}
                  >
                    <Pill size={16} />
                  </div>

                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div
                      style={{
                        fontWeight: 600,
                        color: "#1f2937",
                        marginBottom: 2,
                        fontSize: "clamp(16px, 5.5vw, 22px)",
                        lineHeight: 1.2,
                      }}
                    >
                      {medication.name}
                    </div>
                    <div style={{ fontSize: 12, color: "#6b7280" }}>
                      {medication.reason || "Medication tablet type"}
                    </div>
                    <div
                      style={{
                        fontSize: "clamp(14px, 4.8vw, 17px)",
                        color: "var(--primary)",
                        marginTop: 2,
                        fontWeight: 600,
                      }}
                    >
                      1 tablet at {medication.time}
                    </div>
                    <div style={{ fontSize: 12, color: "#8a93a3", marginTop: 2 }}>
                      {medication.dosage || "65mg"} • {medication.frequency || "After Meal"}
                    </div>
                  </div>
                </div>

                {/* Action row: Taken / Skipped / Complete */}
                <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                  <button
                    onClick={() => handleMedicationTaken(medication)}
                    disabled={
                      medicationActionKey === medication.id ||
                      medicationActionKey === `${medication.id}-skip` ||
                      medicationActionKey === `${medication.id}-complete`
                    }
                    style={{
                      flex: 1,
                      borderRadius: 12,
                      border: "1px solid white",
                      background: "white",
                      color: "var(--primary)",
                      fontSize: 14,
                      fontWeight: 600,
                      padding: "7px 0",
                    }}
                  >
                    {medicationActionKey === medication.id ? "Saving..." : "Taken"}
                  </button>

                  <button
                    onClick={() => handleMedicationSkip(medication)}
                    disabled={
                      medicationActionKey === medication.id ||
                      medicationActionKey === `${medication.id}-skip` ||
                      medicationActionKey === `${medication.id}-complete`
                    }
                    style={{
                      flex: 1,
                      borderRadius: 12,
                      border: "1px solid #d7dbe4",
                      background: "#f0f2f5",
                      color: "#6b7280",
                      fontSize: 14,
                      fontWeight: 600,
                      padding: "7px 0",
                    }}
                  >
                    {medicationActionKey === `${medication.id}-skip` ? "Skipping..." : "Skipped"}
                  </button>

                  <button
                    onClick={() => handleMedicationComplete(medication)}
                    disabled={
                      medicationActionKey === medication.id ||
                      medicationActionKey === `${medication.id}-skip` ||
                      medicationActionKey === `${medication.id}-complete`
                    }
                    style={{
                      flex: 1,
                      borderRadius: 12,
                      border: "1px solid #c8e6c9",
                      background: "#e8f5e9",
                      color: "#2e7d32",
                      fontSize: 14,
                      fontWeight: 600,
                      padding: "7px 0",
                    }}
                  >
                    {medicationActionKey === `${medication.id}-complete`
                      ? "Completing..."
                      : "Complete"}
                  </button>
                </div>
              </div>
            ))}

            <div
              onClick={() => navigate("/remote")}
              style={{
                borderTop: "1px solid white",
                marginTop: 8,
                paddingTop: 10,
                textAlign: "center",
                color: "var(--primary)",
                fontWeight: 600,
                fontSize: 16,
                cursor: "pointer",
              }}
            >
              See Medication Schedule
            </div>
          </>
        ) : (
          <div
            style={{
              background: "white",
              borderRadius: 18,
              border: "1px solid #d8dde6",
              padding: 16,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  color: "#6b7280",
                  fontSize: 15,
                  fontWeight: 500,
                  lineHeight: 1.4,
                  marginBottom: 12,
                }}
              >
                You don't have any active medications right now.
              </div>
              <button
                onClick={() => navigate("/remote")}
                style={{
                  border: "none",
                  background: "transparent",
                  color: "var(--primary)",
                  fontSize: 17,
                  fontWeight: 600,
                  padding: 0,
                }}
              >
                Add Medication
              </button>
            </div>

            <div
              style={{
                width: 100,
                height: 100,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <img
                src={pillsImage}
                alt="Medications"
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Top Doctors */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 10,
          marginTop: 15,
        }}
      >
        <h3 style={{ fontSize: "1.1rem", fontWeight: 700, margin: 0 }}>Top Doctor</h3>

        <button
          type="button"
          onClick={() => navigate("/doctor-consult")}
          style={{
            border: "none",
            background: "transparent",
            color: "#6b7280",
            fontWeight: 500,
            fontSize: 16,
            padding: 0,
            cursor: "pointer",
          }}
        >
          See All
        </button>
      </div>

      {loadingNearby ? (
        <div
          className="scrollbar-hide"
          style={{
            display: "flex",
            gap: 12,
            overflowX: "auto",
            scrollSnapType: "x mandatory",
            paddingBottom: 6,
          }}
        >
          {[1, 2].map((i) => (
            <div
              key={i}
              style={{
                width: 248,
                minWidth: 248,
                height: 314,
                borderRadius: 24,
                background: "#f1f1f5",
                flexShrink: 0,
                scrollSnapAlign: "start",
              }}
            />
          ))}
        </div>
      ) : nearbyDoctors.length === 0 ? (
        <p className="text-muted">No nearby doctors found.</p>
      ) : (
        <div
          className="scrollbar-hide"
          style={{
            display: "flex",
            gap: 12,
            overflowX: "auto",
            scrollSnapType: "x mandatory",
            paddingBottom: 6,
          }}
        >
          {nearbyDoctors.map((doc) => (
            <DoctorCard1 key={doc._id} doctor={doc} />
          ))}
        </div>
      )}

      <br />
    </div>
  );
};

export default Upcoming_appointments;